import type { MaipoExtensionRegistryV01, MaipoGlyphOutlineProjectionV01, MaipoGlyphRasterRequestV01, MaipoGlyphRasterResultV01, MaipoHistoryEntryContentV01, MaipoHistoryEntryV01, MaipoHistorySignatureClaimDocumentV01, MaipoHistorySignatureV01, MaipoHistoryV01, MaipoPatchApplicationV01, MaipoPatchRejectionV01, MaipoPatchV01, MaipoPixelMapInspectionV01, MaipoPublicSceneV01, MaipoReadinessReportV01, MaipoTextShapingPlanV01 } from './types';
import { parseMaipoExtensionRegistry, parseMaipoGlyphOutlineProjection, parseMaipoGlyphRasterRequest, parseMaipoGlyphRasterResult, parseMaipoHistory, parseMaipoHistorySignature, parseMaipoPatch, parseMaipoPatchApplication, parseMaipoPatchRejection, parseMaipoPixelMapInspection, parseMaipoPublicScene, parseMaipoReadinessReport, parseMaipoTextShapingPlan } from './validation';

export const MAIPO_CANONICAL_SCHEMA_INSERTIONS = {
  'maipo-scene': 'https://maipo.design/schema/0.1/scene.schema.json',
  'maipo-patch': 'https://maipo.design/schema/0.1/patch.schema.json',
  'maipo-text-shaping-plan': 'https://maipo.design/schema/0.1/text-shaping-plan.schema.json',
  'maipo-glyph-outline-projection': 'https://maipo.design/schema/0.1/glyph-outline-projection.schema.json',
  'maipo-glyph-raster-request': 'https://maipo.design/schema/0.1/glyph-raster-request.schema.json',
  'maipo-pixel-map-inspection': 'https://maipo.design/schema/0.1/pixel-map-inspection.schema.json',
  'maipo-patch-application': 'https://maipo.design/schema/0.1/patch-application.schema.json',
  'maipo-patch-rejection': 'https://maipo.design/schema/0.1/patch-rejection.schema.json',
  'maipo-history': 'https://maipo.design/schema/0.1/history.schema.json',
  'maipo-history-signature': 'https://maipo.design/schema/0.1/history-signature.schema.json',
  'maipo-extension-registry': 'https://maipo.design/schema/0.1/extension-registry.schema.json',
  'maipo-readiness-report': 'https://maipo.design/schema/0.1/readiness-report.schema.json',
} as const;

export const MAIPO_CANONICAL_SCHEMA_INSERTION_CONFLICT = 'reject' as const;

const SCENE_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-scene'];
const PATCH_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-patch'];
const TEXT_SHAPING_PLAN_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-text-shaping-plan'];
const GLYPH_OUTLINE_PROJECTION_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-glyph-outline-projection'];
const GLYPH_RASTER_REQUEST_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-glyph-raster-request'];
const PIXEL_MAP_INSPECTION_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-pixel-map-inspection'];
const PATCH_APPLICATION_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-patch-application'];
const PATCH_REJECTION_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-patch-rejection'];
const HISTORY_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-history'];
const HISTORY_SIGNATURE_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-history-signature'];
const EXTENSION_REGISTRY_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-extension-registry'];
const READINESS_REPORT_SCHEMA = MAIPO_CANONICAL_SCHEMA_INSERTIONS['maipo-readiness-report'];

export const MAIPO_CANONICAL_PROPERTY_ORDERS = {
  // `extensions` predates `extensionDeclarations` in canonical scene bytes.
  // Keep the compatibility order explicit until a versioned scene migration changes it.
  scene: ['$schema', 'format', 'version', 'documentId', 'revision', 'name', 'canvas', 'fonts', 'assets', 'children', 'extensions', 'extensionDeclarations'],
  patch: ['$schema', 'format', 'version', 'documentId', 'baseRevision', 'clientPatchId', 'operations'],
  operation: ['op', 'target', 'path', 'value', 'parent', 'before', 'node'],
  node: ['id', 'type', 'name', 'role', 'visible', 'opacity', 'fillOpacity', 'blendMode', 'locked', 'clipping', 'transform', 'text', 'frame', 'style', 'children', 'objects', 'asset', 'extensions'],
  vectorObject: ['id', 'type', 'name', 'role', 'visible', 'opacity', 'transform', 'fill', 'stroke', 'geometry', 'extensions'],
  canvas: ['width', 'height', 'colorSpace', 'background'],
  transform: ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'skewX'],
  frame: ['width', 'height', 'autoHeight', 'fit'],
  style: ['fontRef', 'fontFamily', 'fontWeight', 'fontSize', 'lineHeight', 'letterSpacing', 'textAlign', 'color'],
  paint: ['type', 'color', 'opacity'],
  stroke: ['paint', 'width', 'lineCap', 'lineJoin', 'dash'],
  geometry: ['x', 'y', 'width', 'height', 'radius', 'nodes', 'subpaths', 'fillRule'],
  radius: ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
  pathNode: ['id', 'subpathId', 'anchor', 'incoming', 'outgoing', 'continuity', 'cornerRadius'],
  subpath: ['id', 'nodeIds', 'closed'],
  point: ['x', 'y'],
  asset: ['id', 'digest', 'mediaType', 'byteLength', 'data'],
  font: ['id', 'family', 'weight', 'style', 'source', 'license'],
  source: ['kind', 'asset'],
  license: ['spdx', 'embedding', 'notice'],
  shapingPlan: ['$schema', 'format', 'version', 'source', 'font', 'model', 'runs'],
  shapingSource: ['documentId', 'revision', 'nodeId', 'text', 'textDigest'],
  shapingFont: ['fontRef', 'digest', 'faceIndex', 'unitsPerEm', 'variations'],
  shapingModel: ['id', 'version', 'clusterUnit', 'clusterLevel'],
  shapingRun: ['id', 'sourceStart', 'sourceEnd', 'direction', 'script', 'language', 'origin', 'features', 'glyphs'],
  shapingOrigin: ['x', 'y'],
  shapingFeature: ['tag', 'value'],
  shapingGlyph: ['id', 'cluster', 'xAdvance', 'yAdvance', 'xOffset', 'yOffset', 'flags'],
  outlineProjection: ['$schema', 'format', 'version', 'planDigest', 'fontDigest', 'unitsPerEm', 'model', 'bounds', 'runs'],
  outlineModel: ['id', 'version', 'coordinateSpace', 'pathModel'],
  outlineRun: ['runId', 'glyphs'],
  outlineGlyph: ['glyphId', 'cluster', 'origin', 'bounds', 'path'],
  outlineBounds: ['xMin', 'yMin', 'xMax', 'yMax'],
  outlineCommand: ['op', 'values'],
  glyphRasterRequest: ['$schema', 'format', 'version', 'requestId', 'projection', 'options'],
  glyphRasterOptions: ['width', 'height', 'padding', 'fit', 'fill', 'background', 'includeGlyphMap'],
  glyphRasterBackground: ['type', 'color'],
  pixelMapInspection: ['$schema', 'format', 'version', 'source', 'region', 'pixelCount', 'unownedPixels', 'owners'],
  pixelMapSource: ['width', 'height', 'digest', 'ids'],
  pixelMapRegion: ['x', 'y', 'width', 'height'],
  pixelMapOwner: ['owner', 'id', 'pixels', 'coverage', 'bounds'],
  patchApplication: ['$schema', 'format', 'version', 'status', 'documentId', 'clientPatchId', 'baseRevision', 'resultRevision', 'operationCount', 'sourceSceneDigest', 'patchDigest', 'resultSceneDigest'],
  patchRejection: ['$schema', 'format', 'version', 'status', 'documentId', 'sourceDocumentId', 'clientPatchId', 'baseRevision', 'sourceRevision', 'operationCount', 'sourceSceneDigest', 'patchDigest', 'reason', 'operationIndex', 'target', 'parent', 'before', 'path'],
  history: ['$schema', 'format', 'version', 'documentId', 'genesis', 'entries'],
  historyGenesis: ['revision', 'sceneDigest'],
  historyEntry: ['sequence', 'previousEntryDigest', 'entryDigest', 'application'],
  historyEntryContent: ['format', 'version', 'sequence', 'previousEntryDigest', 'application'],
  historySignature: ['$schema', 'format', 'version', 'target', 'claim', 'signer', 'algorithm', 'publicKey', 'keyFingerprint', 'signature'],
  historySignatureClaim: ['format', 'version', 'target', 'claim', 'signer', 'algorithm', 'publicKey', 'keyFingerprint'],
  historyTarget: ['documentId', 'sequence', 'entryDigest'],
  historySigner: ['id', 'name'],
  extensionRegistry: ['$schema', 'format', 'version', 'sceneVersion', 'policy', 'reserved', 'entries'],
  extensionPolicy: ['execution', 'networkAccess', 'renderingEffect', 'unknownBehavior', 'maipoNamespace', 'externalNamespace'],
  extensionReserved: ['key', 'owner', 'purpose'],
  extensionEntry: ['key', 'version', 'kind', 'status', 'owner', 'purpose', 'schema', 'security'],
  extensionSchema: ['id', 'digest'],
  extensionSecurity: ['executable', 'networkAccess', 'affectsRendering'],
  // This is the experimental 0.1 byte order, not JSON Schema declaration order.
  extensionDeclaration: ['key', 'kind', 'required', 'schemaId', 'version'],
  readinessReport: ['$schema', 'format', 'version', 'candidate', 'assessedPublication', 'status', 'summary', 'requirements'],
  readinessSummary: ['achieved', 'partial', 'missing', 'total'],
  readinessRequirement: ['id', 'requirement', 'status', 'evidence', 'gaps', 'exitCriteria'],
  readinessEvidence: ['kind', 'ref'],
} as const;

const ORDERS = MAIPO_CANONICAL_PROPERTY_ORDERS;

export type MaipoCanonicalPropertyOrderName = keyof typeof MAIPO_CANONICAL_PROPERTY_ORDERS;

export interface MaipoCanonicalOrderSelector {
  order: MaipoCanonicalPropertyOrderName;
  format?: string;
  parent?: string;
  requiredProperties?: readonly string[];
  property?: { name: string; values: readonly string[] };
}

/** First match wins. This is the public, language-neutral dispatch table. */
export const MAIPO_CANONICAL_ORDER_SELECTORS: readonly MaipoCanonicalOrderSelector[] = [
  { order: 'scene', format: 'maipo-scene' },
  { order: 'patch', format: 'maipo-patch' },
  { order: 'shapingPlan', format: 'maipo-text-shaping-plan' },
  { order: 'outlineProjection', format: 'maipo-glyph-outline-projection' },
  { order: 'glyphRasterRequest', format: 'maipo-glyph-raster-request' },
  { order: 'pixelMapInspection', format: 'maipo-pixel-map-inspection' },
  { order: 'patchApplication', format: 'maipo-patch-application' },
  { order: 'patchRejection', format: 'maipo-patch-rejection' },
  { order: 'history', format: 'maipo-history' },
  { order: 'historyEntryContent', format: 'maipo-history-entry-content' },
  { order: 'historySignature', format: 'maipo-history-signature' },
  { order: 'historySignatureClaim', format: 'maipo-history-signature-claim' },
  { order: 'extensionRegistry', format: 'maipo-extension-registry' },
  { order: 'readinessReport', format: 'maipo-readiness-report' },
  { order: 'extensionPolicy', parent: 'policy', requiredProperties: ['unknownBehavior'] },
  { order: 'extensionReserved', parent: 'reserved' },
  { order: 'extensionDeclaration', parent: 'extensionDeclarations' },
  { order: 'extensionEntry', parent: 'entries', requiredProperties: ['security'] },
  { order: 'extensionSchema', parent: 'schema', requiredProperties: ['digest'] },
  { order: 'extensionSecurity', parent: 'security' },
  { order: 'readinessSummary', parent: 'summary' },
  { order: 'readinessRequirement', parent: 'requirements' },
  { order: 'readinessEvidence', parent: 'evidence' },
  { order: 'shapingSource', parent: 'source', requiredProperties: ['textDigest'] },
  { order: 'shapingFont', parent: 'font', requiredProperties: ['unitsPerEm'] },
  { order: 'outlineModel', parent: 'model', requiredProperties: ['pathModel'] },
  { order: 'shapingModel', parent: 'model' },
  { order: 'outlineRun', parent: 'runs', requiredProperties: ['runId'] },
  { order: 'shapingRun', parent: 'runs' },
  { order: 'shapingOrigin', parent: 'origin' },
  { order: 'shapingFeature', parent: 'features' },
  { order: 'shapingFeature', parent: 'variations' },
  { order: 'outlineGlyph', parent: 'glyphs', requiredProperties: ['glyphId'] },
  { order: 'shapingGlyph', parent: 'glyphs' },
  { order: 'outlineBounds', parent: 'bounds', requiredProperties: ['xMin'] },
  { order: 'pixelMapRegion', parent: 'bounds' },
  { order: 'outlineCommand', parent: 'path' },
  { order: 'glyphRasterOptions', parent: 'options', requiredProperties: ['includeGlyphMap'] },
  { order: 'glyphRasterBackground', parent: 'background' },
  { order: 'pixelMapSource', parent: 'source', requiredProperties: ['digest', 'ids'] },
  { order: 'pixelMapRegion', parent: 'region' },
  { order: 'pixelMapOwner', parent: 'owners' },
  { order: 'historyGenesis', parent: 'genesis' },
  { order: 'historyEntry', parent: 'entries' },
  { order: 'historyTarget', parent: 'target', requiredProperties: ['entryDigest'] },
  { order: 'historySigner', parent: 'signer' },
  { order: 'operation', requiredProperties: ['op'] },
  { order: 'canvas', parent: 'canvas' },
  { order: 'transform', parent: 'transform' },
  { order: 'frame', parent: 'frame' },
  { order: 'style', parent: 'style', requiredProperties: ['fontFamily'] },
  { order: 'paint', parent: 'paint' },
  { order: 'paint', property: { name: 'type', values: ['solid'] }, requiredProperties: ['color'] },
  { order: 'stroke', parent: 'stroke' },
  { order: 'geometry', parent: 'geometry' },
  { order: 'radius', parent: 'radius' },
  { order: 'pathNode', requiredProperties: ['subpathId'] },
  { order: 'subpath', requiredProperties: ['nodeIds'] },
  { order: 'point', parent: 'anchor', requiredProperties: ['x'] },
  { order: 'point', parent: 'incoming', requiredProperties: ['x'] },
  { order: 'point', parent: 'outgoing', requiredProperties: ['x'] },
  { order: 'asset', requiredProperties: ['digest'] },
  { order: 'font', requiredProperties: ['family', 'source'] },
  { order: 'source', parent: 'source' },
  { order: 'license', parent: 'license' },
  { order: 'node', property: { name: 'type', values: ['group', 'text', 'vector', 'image'] } },
  { order: 'vectorObject', property: { name: 'type', values: ['rectangle', 'ellipse', 'path'] } },
] as const;

export const MAIPO_CANONICAL_ARRAY_SORTS = [
  { parent: 'variations', property: 'tag', comparison: 'utf16-code-unit' },
  { parent: 'features', property: 'tag', comparison: 'utf16-code-unit' },
] as const;

export const MAIPO_CANONICAL_OMISSION_RULES = [
  { parent: 'transform', properties: ['x', 'y', 'rotation', 'skewX'], value: 0 },
  { parent: 'transform', properties: ['scaleX', 'scaleY'], value: 1 },
  { properties: ['visible'], value: true },
  { properties: ['opacity', 'fillOpacity'], value: 1 },
  { properties: ['blendMode'], value: 'normal' },
  { properties: ['locked', 'clipping', 'autoHeight'], value: false },
  { parent: 'extensionDeclarations', properties: ['required'], value: false },
  { properties: ['fill', 'stroke'], value: null },
  { parent: 'glyphs', properties: ['flags'], value: [] },
] as const;

export const MAIPO_CANONICAL_EMPTY_OBJECT_OMISSIONS = ['transform'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeNumber(value: number): number {
  if (!Number.isFinite(value)) throw new Error('Maipo canonical JSON cannot encode a non-finite number.');
  return Object.is(value, -0) ? 0 : value;
}

function assertUnicodeScalarString(value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) throw new Error('Maipo canonical JSON cannot encode an unpaired UTF-16 surrogate.');
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      throw new Error('Maipo canonical JSON cannot encode an unpaired UTF-16 surrogate.');
    }
  }
}

function canonicalRuleValueEquals(value: unknown, expected: unknown): boolean {
  if (Array.isArray(expected)) return Array.isArray(value) && value.length === 0 && expected.length === 0;
  return value === expected;
}

function isSchemaDefault(parentKey: string | undefined, key: string, value: unknown): boolean {
  return MAIPO_CANONICAL_OMISSION_RULES.some((rule) => (
    (!('parent' in rule) || rule.parent === parentKey)
    && (rule.properties as readonly string[]).includes(key)
    && canonicalRuleValueEquals(value, rule.value)
  ));
}

function objectOrder(value: Record<string, unknown>, parentKey?: string): readonly string[] {
  for (const selector of MAIPO_CANONICAL_ORDER_SELECTORS) {
    if (selector.format !== undefined && value.format !== selector.format) continue;
    if (selector.parent !== undefined && parentKey !== selector.parent) continue;
    if (selector.requiredProperties?.some((property) => !(property in value))) continue;
    if (selector.property !== undefined && !selector.property.values.includes(value[selector.property.name] as string)) continue;
    return ORDERS[selector.order];
  }
  return [];
}

function canonicalSchema(value: Record<string, unknown>): string | undefined {
  if (typeof value.format !== 'string') return undefined;
  return (MAIPO_CANONICAL_SCHEMA_INSERTIONS as Readonly<Record<string, string>>)[value.format];
}

function compareKeys(order: readonly string[], left: string, right: string): number {
  const leftRank = order.indexOf(left);
  const rightRank = order.indexOf(right);
  const hasLeft = leftRank >= 0;
  const hasRight = rightRank >= 0;
  if (hasLeft || hasRight) {
    if (!hasLeft) return 1;
    if (!hasRight) return -1;
    if (leftRank !== rightRank) return leftRank - rightRank;
  }
  return compareUtf16CodeUnits(left, right);
}

export function compareUtf16CodeUnits(left: string, right: string): number {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function canonicalValue(value: unknown, parentKey?: string, extensionPayload = false): unknown {
  if (typeof value === 'number') return normalizeNumber(value);
  if (typeof value === 'string') {
    assertUnicodeScalarString(value);
    return value;
  }
  if (Array.isArray(value)) {
    const normalized = value.map((entry) => canonicalValue(entry, parentKey, extensionPayload));
    const sort = extensionPayload ? undefined : MAIPO_CANONICAL_ARRAY_SORTS.find((rule) => rule.parent === parentKey);
    if (sort) normalized.sort((left, right) => {
      if (!isRecord(left) || !isRecord(right)) return 0;
      const leftKey = left[sort.property];
      const rightKey = right[sort.property];
      if (typeof leftKey !== 'string' || typeof rightKey !== 'string') return 0;
      return compareUtf16CodeUnits(leftKey, rightKey);
    });
    return normalized;
  }
  if (!isRecord(value)) return value;

  const insertedSchema = extensionPayload ? undefined : canonicalSchema(value);
  const authored = insertedSchema === undefined || value.$schema !== undefined ? value : { ...value, $schema: insertedSchema };
  const result: Record<string, unknown> = {};
  const order = extensionPayload ? [] : objectOrder(authored, parentKey);
  for (const key of Object.keys(authored).sort((left, right) => compareKeys(order, left, right))) {
    assertUnicodeScalarString(key);
    const child = authored[key];
    if (child === undefined || (!extensionPayload && isSchemaDefault(parentKey, key, child))) continue;
    const normalized = canonicalValue(child, key, extensionPayload || key === 'extensions');
    if (!extensionPayload && (MAIPO_CANONICAL_EMPTY_OBJECT_OMISSIONS as readonly string[]).includes(key) && isRecord(normalized) && Object.keys(normalized).length === 0) continue;
    result[key] = normalized;
  }
  return result;
}

export function canonicalizeMaipoPublicScene(value: unknown): MaipoPublicSceneV01 {
  const scene = structuredClone(parseMaipoPublicScene(canonicalValue(value)));
  scene.$schema = SCENE_SCHEMA;
  return canonicalValue(scene) as MaipoPublicSceneV01;
}

export function canonicalizeMaipoPatch(value: unknown): MaipoPatchV01 {
  const patch = structuredClone(parseMaipoPatch(canonicalValue(value)));
  patch.$schema = PATCH_SCHEMA;
  return canonicalValue(patch) as MaipoPatchV01;
}

export function serializeMaipoPublicScene(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoPublicScene(value), null, 2)}\n`;
}

export function serializeMaipoPatch(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoPatch(value), null, 2)}\n`;
}

export function canonicalizeMaipoTextShapingPlan(value: unknown): MaipoTextShapingPlanV01 {
  const plan = structuredClone(parseMaipoTextShapingPlan(canonicalValue(value)));
  plan.$schema = TEXT_SHAPING_PLAN_SCHEMA;
  return canonicalValue(plan) as MaipoTextShapingPlanV01;
}

export function serializeMaipoTextShapingPlan(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoTextShapingPlan(value), null, 2)}\n`;
}

export function canonicalizeMaipoGlyphOutlineProjection(value: unknown): MaipoGlyphOutlineProjectionV01 {
  const projection = structuredClone(parseMaipoGlyphOutlineProjection(canonicalValue(value)));
  projection.$schema = GLYPH_OUTLINE_PROJECTION_SCHEMA;
  return canonicalValue(projection) as MaipoGlyphOutlineProjectionV01;
}

export function serializeMaipoGlyphOutlineProjection(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoGlyphOutlineProjection(value), null, 2)}\n`;
}

export function canonicalizeMaipoGlyphRasterRequest(value: unknown): MaipoGlyphRasterRequestV01 {
  const request = structuredClone(parseMaipoGlyphRasterRequest(canonicalValue(value)));
  request.$schema = GLYPH_RASTER_REQUEST_SCHEMA;
  request.projection = canonicalizeMaipoGlyphOutlineProjection(request.projection);
  return canonicalValue(request) as MaipoGlyphRasterRequestV01;
}

export function serializeMaipoGlyphRasterRequest(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoGlyphRasterRequest(value), null, 2)}\n`;
}

export function canonicalizeMaipoPixelMapInspection(value: unknown): MaipoPixelMapInspectionV01 {
  const inspection = structuredClone(parseMaipoPixelMapInspection(canonicalValue(value)));
  inspection.$schema = PIXEL_MAP_INSPECTION_SCHEMA;
  return canonicalValue(inspection) as MaipoPixelMapInspectionV01;
}

export function serializeMaipoPixelMapInspection(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoPixelMapInspection(value), null, 2)}\n`;
}

export function canonicalizeMaipoPatchApplication(value: unknown): MaipoPatchApplicationV01 {
  const application = structuredClone(parseMaipoPatchApplication(canonicalValue(value)));
  application.$schema = PATCH_APPLICATION_SCHEMA;
  return canonicalValue(application) as MaipoPatchApplicationV01;
}

export function serializeMaipoPatchApplication(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoPatchApplication(value), null, 2)}\n`;
}

export function canonicalizeMaipoPatchRejection(value: unknown): MaipoPatchRejectionV01 {
  const rejection = structuredClone(parseMaipoPatchRejection(canonicalValue(value)));
  rejection.$schema = PATCH_REJECTION_SCHEMA;
  return canonicalValue(rejection) as MaipoPatchRejectionV01;
}

export function serializeMaipoPatchRejection(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoPatchRejection(value), null, 2)}\n`;
}

export function canonicalizeMaipoHistory(value: unknown): MaipoHistoryV01 {
  const history = structuredClone(parseMaipoHistory(canonicalValue(value)));
  history.$schema = HISTORY_SCHEMA;
  return canonicalValue(history) as MaipoHistoryV01;
}

export function serializeMaipoHistory(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoHistory(value), null, 2)}\n`;
}

export function serializeMaipoHistoryEntryContent(value: MaipoHistoryEntryV01): string {
  if (!Number.isInteger(value.sequence) || value.sequence < 1 || value.sequence > 10000) throw new Error('Maipo history entry sequence is invalid.');
  if (value.previousEntryDigest !== null && !/^sha256:[0-9a-f]{64}$/.test(value.previousEntryDigest)) throw new Error('Maipo history predecessor digest is invalid.');
  const content: MaipoHistoryEntryContentV01 = {
    format: 'maipo-history-entry-content',
    version: '0.1',
    sequence: value.sequence,
    previousEntryDigest: value.previousEntryDigest,
    application: structuredClone(parseMaipoPatchApplication(canonicalValue(value.application))),
  };
  return `${JSON.stringify(canonicalValue(content), null, 2)}\n`;
}

export function canonicalizeMaipoHistorySignature(value: unknown): MaipoHistorySignatureV01 {
  const signature = structuredClone(parseMaipoHistorySignature(canonicalValue(value)));
  signature.$schema = HISTORY_SIGNATURE_SCHEMA;
  return canonicalValue(signature) as MaipoHistorySignatureV01;
}

export function serializeMaipoHistorySignature(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoHistorySignature(value), null, 2)}\n`;
}

export function serializeMaipoHistorySignatureClaim(value: MaipoHistorySignatureClaimDocumentV01): string {
  parseMaipoHistorySignature({
    $schema: HISTORY_SIGNATURE_SCHEMA,
    format: 'maipo-history-signature',
    version: '0.1',
    target: value.target,
    claim: value.claim,
    signer: value.signer,
    algorithm: value.algorithm,
    publicKey: value.publicKey,
    keyFingerprint: value.keyFingerprint,
    signature: 'A'.repeat(86),
  });
  const claim: MaipoHistorySignatureClaimDocumentV01 = {
    format: 'maipo-history-signature-claim',
    version: '0.1',
    target: structuredClone(value.target),
    claim: value.claim,
    signer: structuredClone(value.signer),
    algorithm: value.algorithm,
    publicKey: value.publicKey,
    keyFingerprint: value.keyFingerprint,
  };
  return `${JSON.stringify(canonicalValue(claim), null, 2)}\n`;
}

export function canonicalizeMaipoExtensionRegistry(value: unknown): MaipoExtensionRegistryV01 {
  const registry = structuredClone(parseMaipoExtensionRegistry(canonicalValue(value)));
  registry.$schema = EXTENSION_REGISTRY_SCHEMA;
  return canonicalValue(registry) as MaipoExtensionRegistryV01;
}

export function serializeMaipoExtensionRegistry(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoExtensionRegistry(value), null, 2)}\n`;
}

export function canonicalizeMaipoReadinessReport(value: unknown): MaipoReadinessReportV01 {
  const report = structuredClone(parseMaipoReadinessReport(canonicalValue(value)));
  report.$schema = READINESS_REPORT_SCHEMA;
  return canonicalValue(report) as MaipoReadinessReportV01;
}

export function serializeMaipoReadinessReport(value: unknown): string {
  return `${JSON.stringify(canonicalizeMaipoReadinessReport(value), null, 2)}\n`;
}

async function canonicalDigest(bytes: Uint8Array): Promise<string> {
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return `sha256:${[...hash].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

export async function verifyMaipoGlyphOutlineProjection(
  value: unknown,
  options: { plan?: unknown; fontBytes?: Uint8Array } = {},
): Promise<MaipoGlyphOutlineProjectionV01> {
  const projection = parseMaipoGlyphOutlineProjection(value);
  if (options.fontBytes && await canonicalDigest(options.fontBytes) !== projection.fontDigest) throw new Error('Maipo glyph outline projection font bytes do not match its SHA-256 digest.');
  if (options.plan !== undefined) {
    const plan = parseMaipoTextShapingPlan(options.plan);
    const canonicalPlan = new TextEncoder().encode(serializeMaipoTextShapingPlan(plan));
    if (await canonicalDigest(canonicalPlan) !== projection.planDigest) throw new Error('Maipo glyph outline projection plan does not match its SHA-256 digest.');
    if (plan.font.digest !== projection.fontDigest || plan.font.unitsPerEm !== projection.unitsPerEm) throw new Error('Maipo glyph outline projection font identity differs from its shaping plan.');
    if (plan.runs.length !== projection.runs.length) throw new Error('Maipo glyph outline projection run count differs from its shaping plan.');
    for (let runIndex = 0; runIndex < plan.runs.length; runIndex += 1) {
      const planRun = plan.runs[runIndex]!;
      const projectedRun = projection.runs[runIndex]!;
      if (planRun.id !== projectedRun.runId || planRun.glyphs.length !== projectedRun.glyphs.length) throw new Error('Maipo glyph outline projection run identity differs from its shaping plan.');
      let penX = planRun.origin.x;
      let penY = planRun.origin.y;
      for (let glyphIndex = 0; glyphIndex < planRun.glyphs.length; glyphIndex += 1) {
        const planGlyph = planRun.glyphs[glyphIndex]!;
        const projectedGlyph = projectedRun.glyphs[glyphIndex]!;
        if (
          planGlyph.id !== projectedGlyph.glyphId
          || planGlyph.cluster !== projectedGlyph.cluster
          || penX + planGlyph.xOffset !== projectedGlyph.origin.x
          || penY + planGlyph.yOffset !== projectedGlyph.origin.y
        ) throw new Error('Maipo glyph outline projection glyph identity or origin differs from its shaping plan.');
        penX += planGlyph.xAdvance;
        penY += planGlyph.yAdvance;
      }
    }
  }
  return projection;
}

function canonicalBase64Bytes(value: string, label: string): Uint8Array {
  if (value.length % 4 !== 0 || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) throw new Error(`${label} is not canonical base64.`);
  try {
    const binary = atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new Error(`${label} is not valid base64.`);
  }
}

export async function verifyMaipoGlyphRasterResult(
  value: unknown,
  options: { request?: unknown; rgbaBytes?: Uint8Array } = {},
): Promise<MaipoGlyphRasterResultV01> {
  const result = parseMaipoGlyphRasterResult(value);
  const artifact = canonicalBase64Bytes(result.artifact.data, 'Maipo glyph raster PNG');
  if (artifact.byteLength !== result.artifact.byteLength) throw new Error('Maipo glyph raster PNG byte length does not match its data.');
  if (await canonicalDigest(artifact) !== result.artifact.digest) throw new Error('Maipo glyph raster PNG does not match its SHA-256 digest.');
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (artifact.byteLength < 24 || !signature.every((byte, index) => artifact[index] === byte)) throw new Error('Maipo glyph raster artifact has no PNG signature.');
  const artifactView = new DataView(artifact.buffer, artifact.byteOffset, artifact.byteLength);
  if (artifactView.getUint32(8) !== 13 || String.fromCharCode(...artifact.subarray(12, 16)) !== 'IHDR') throw new Error('Maipo glyph raster artifact has no canonical first IHDR chunk.');
  if (artifactView.getUint32(16) !== result.artifact.width || artifactView.getUint32(20) !== result.artifact.height) throw new Error('Maipo glyph raster PNG dimensions differ from its result metadata.');
  if (artifactView.getUint8(24) !== 8 || artifactView.getUint8(25) !== 6 || artifactView.getUint8(26) !== 0 || artifactView.getUint8(27) !== 0 || artifactView.getUint8(28) !== 0) {
    throw new Error('Maipo glyph raster PNG must be non-interlaced RGBA8 with standard compression and filtering.');
  }
  if (result.glyphMap) {
    const map = canonicalBase64Bytes(result.glyphMap.data, 'Maipo glyph raster map');
    const expectedBytes = result.glyphMap.width * result.glyphMap.height * 4;
    if (!Number.isSafeInteger(expectedBytes) || map.byteLength !== expectedBytes || result.glyphMap.byteLength !== expectedBytes) throw new Error('Maipo glyph raster map dimensions and byte length disagree.');
    if (await canonicalDigest(map) !== result.glyphMap.digest) throw new Error('Maipo glyph raster map does not match its SHA-256 digest.');
    const mapView = new DataView(map.buffer, map.byteOffset, map.byteLength);
    for (let offset = 0; offset < map.byteLength; offset += 4) {
      const owner = mapView.getUint32(offset, true);
      if (owner > result.glyphMap.ids.length) throw new Error('Maipo glyph raster map contains an undeclared pixel owner.');
      if (owner > 0 && !result.glyphs[owner - 1]!.visible) throw new Error('Maipo glyph raster map assigns pixels to a pathless glyph.');
    }
  }
  if (options.rgbaBytes) {
    const expectedBytes = result.artifact.width * result.artifact.height * 4;
    if (options.rgbaBytes.byteLength !== expectedBytes) throw new Error('Maipo glyph raster RGBA dimensions and byte length disagree.');
    if (await canonicalDigest(options.rgbaBytes) !== result.pixelDigest) throw new Error('Maipo glyph raster RGBA bytes do not match its pixel digest.');
    for (let offset = 0; offset < options.rgbaBytes.byteLength; offset += 4) if (options.rgbaBytes[offset + 3] === 0 && (options.rgbaBytes[offset] !== 0 || options.rgbaBytes[offset + 1] !== 0 || options.rgbaBytes[offset + 2] !== 0)) {
      throw new Error('Maipo glyph raster transparent pixels contain nonzero RGB channels.');
    }
  }
  if (options.request !== undefined) {
    const request = parseMaipoGlyphRasterRequest(options.request);
    if (result.requestId !== request.requestId || JSON.stringify(canonicalValue(result.options, 'options')) !== JSON.stringify(canonicalValue(request.options, 'options'))) throw new Error('Maipo glyph raster result identity or options differ from its request.');
    const projectionDigest = await canonicalDigest(new TextEncoder().encode(serializeMaipoGlyphOutlineProjection(request.projection)));
    if (result.projectionDigest !== projectionDigest) throw new Error('Maipo glyph raster result projection digest differs from its request.');
    const expectedGlyphs = request.projection.runs.flatMap((run) => run.glyphs.map((glyph, glyphIndex) => ({
      id: `${run.runId}/glyph/${String(glyphIndex)}`,
      runId: run.runId,
      glyphIndex,
      glyphId: glyph.glyphId,
      cluster: glyph.cluster,
      visible: glyph.path.length > 0,
    })));
    if (result.glyphs.length !== expectedGlyphs.length) throw new Error('Maipo glyph raster result glyph count differs from its request.');
    for (let index = 0; index < expectedGlyphs.length; index += 1) {
      const expected = expectedGlyphs[index]!;
      const actual = result.glyphs[index]!;
      if (actual.id !== expected.id || actual.runId !== expected.runId || actual.glyphIndex !== expected.glyphIndex || actual.glyphId !== expected.glyphId || actual.cluster !== expected.cluster || actual.visible !== expected.visible) {
        throw new Error('Maipo glyph raster result glyph identity differs from its request.');
      }
    }
  }
  return result;
}
